import classes from './Logo.module.css';

export type LogoVariant = 'mark' | 'wordmark' | 'lockup' | 'vertical';

/**
 * The Black Arrow logo. Never wrap it in a tile, squircle or badge: the three
 * arrows are the icon.
 *
 * - `mark`: the arrows alone (aspect 70:111).
 * - `wordmark`: the name alone.
 * - `lockup`: arrows left, name right. The default for headers.
 * - `vertical`: arrows over the name, for sign-in, splash and tall spaces.
 *
 * Size it by height, through `className` or `height` (px). Colour is ink and
 * follows the colour scheme; `onDark` pins it to white for the fixed-dark pages.
 * Hover fades it one step lighter.
 */
export function Logo({
    variant = 'lockup',
    onDark = false,
    centered = false,
    height,
    className,
}: {
    variant?: LogoVariant;
    onDark?: boolean;
    /** Centre the logo in a box the caller sized, instead of left-aligning it. */
    centered?: boolean;
    height?: number | undefined;
    className?: string | undefined;
}) {
    const rootClass = [
        classes.root,
        variant === 'vertical' ? classes.vertical : '',
        onDark ? classes.onDark : '',
        centered ? classes.centered : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <span
            className={rootClass}
            role="img"
            aria-label="Black Arrow"
            style={height === undefined ? undefined : { height }}
        >
            {variant === 'wordmark' ? (
                <span className={`${classes.part} ${classes.wordOnly}`} />
            ) : (
                <span className={`${classes.part} ${classes.mark}`} />
            )}
            {variant === 'lockup' ? <span className={`${classes.part} ${classes.word}`} /> : null}
            {variant === 'vertical' ? (
                <span className={`${classes.part} ${classes.wordOnly}`} />
            ) : null}
        </span>
    );
}
